import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { ButtonBase, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { PaymentsListItemPill } from "@src/appV2/redesign/Payments/Earnings/components/PaymentsListItemPill";
import { type PaymentsListItem } from "@src/appV2/redesign/Payments/Earnings/types/PaymentsListItem";
import { PAYMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Payments/PaymentDetails/paths";
import { getExpectedPayoutDate } from "@src/appV2/redesign/Payments/utils/getExpectedPayoutDate";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { generatePath, useHistory } from "react-router-dom";

interface PaymentHistoryItemProps {
  item: PaymentsListItem;
}

function getDateLabel(item: PaymentsListItem): string {
  const createdAt = new Date(item.attributes.createdAt);

  if (item.attributes.payoutStatus === "pending") {
    const isPayoutInstant = item.attributes.payoutMethod === "instant";
    const expectedDate = getExpectedPayoutDate({ paymentDate: createdAt, isPayoutInstant });

    return `Expected by ${
      isPayoutInstant ? formatTime(expectedDate) : formatShortDateWithWeekday(expectedDate)
    }`;
  }

  return `${formatShortDateWithWeekday(createdAt)} \u2022 ${formatTime(createdAt)}`;
}

export function PaymentHistoryItem(props: PaymentHistoryItemProps) {
  const { item } = props;
  const history = useHistory();
  const formattedAmount = formatDollarsAsUsd(item.attributes.amountInCents / 100);

  const handleClick = () => {
    history.push(generatePath(PAYMENT_DETAILS_FULL_PATH, { paymentId: item.id }));
  };

  return (
    <Stack
      sx={(theme) => ({
        paddingY: theme.spacing(4),
      })}
    >
      <ButtonBase
        aria-label={`View earnings details for ${formattedAmount}`}
        sx={{ width: "100%", textAlign: "left" }}
        onClick={handleClick}
      >
        <Stack direction="row" alignItems="flex-start" sx={{ width: "100%" }}>
          <Stack
            gap={2}
            sx={(theme) => ({
              flex: 1,
              marginRight: theme.spacing(3),
            })}
          >
            <Text semibold variant="body1">
              {formattedAmount}
            </Text>
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {getDateLabel(item)}
            </Text>
          </Stack>
          <PaymentsListItemPill item={item} />
        </Stack>
      </ButtonBase>
    </Stack>
  );
}
